import { getCalendarWeek } from "@/lib/daycare-hotel/stayPlans";
import { isActivePlanWeek } from "@/lib/daycare-hotel/planFixedDays";

/**
 * O ciclo de um plano semanal visto semana a semana — o que o card mostra no
 * lugar da cota só da semana corrente. Cada diária vira um marcador com data,
 * para a recepção ver o mês inteiro sem abrir a agenda.
 */
export type CycleDayState = "used" | "scheduled" | "available" | "extra";

export interface CycleWeekDay {
  /** Vazio nos marcadores de vaga livre: eles não têm data ainda. */
  date: string | null;
  state: CycleDayState;
}

export interface CycleWeek {
  start: string;
  end: string;
  /** Semana corrente do ciclo, destacada no card. */
  isCurrent: boolean;
  /** Semana de folga do plano quinzenal: sem cota e sem vagas. */
  isOffWeek: boolean;
  days: CycleWeekDay[];
}

export interface CycleWeeksInput {
  cycle: { start: string; end: string };
  today: string;
  /** Diárias do contrato; `hold` é reserva e `checkin` é diária utilizada. */
  usages: Array<{ usedOn: string; units: number; source: "hold" | "checkin" }>;
  /** Cota contratada por semana. */
  weeklyLimit: number;
  /** Início do contrato: ancora as semanas ativas do quinzenal. */
  startsOn: string;
  /** 2 = a cada 15 dias. */
  weekInterval?: number;
}

function addDays(date: string, amount: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + amount));
  return next.toISOString().slice(0, 10);
}

/**
 * Semanas do ciclo com os marcadores de cada uma.
 *
 * Numa semana já passada não sobram vagas: o que não foi usado expirou com ela,
 * e mostrar espaço livre no passado só confunde. As diárias além da cota
 * aparecem como extra, que é como elas foram cobradas.
 */
export function buildCycleWeeks({
  cycle,
  today,
  usages,
  weeklyLimit,
  startsOn,
  weekInterval = 1,
}: CycleWeeksInput): CycleWeek[] {
  const weeks: CycleWeek[] = [];
  const currentWeek = getCalendarWeek(today);

  for (let cursor = getCalendarWeek(cycle.start).start; cursor <= cycle.end; cursor = addDays(cursor, 7)) {
    const week = getCalendarWeek(cursor);
    const isOffWeek = !isActivePlanWeek(startsOn, cursor, weekInterval);
    const inWeek = usages
      .filter((usage) => usage.usedOn >= week.start && usage.usedOn <= week.end
        && usage.usedOn >= cycle.start && usage.usedOn <= cycle.end)
      .sort((a, b) => a.usedOn.localeCompare(b.usedOn));

    const days: CycleWeekDay[] = inWeek.map((usage, index) => ({
      date: usage.usedOn,
      state: index >= weeklyLimit || isOffWeek
        ? "extra"
        : usage.source === "checkin"
          ? "used"
          : "scheduled",
    }));

    const isPast = week.end < currentWeek.start;
    const freeSlots = isOffWeek || isPast ? 0 : Math.max(0, weeklyLimit - inWeek.length);
    for (let index = 0; index < freeSlots; index += 1) days.push({ date: null, state: "available" });

    weeks.push({ start: week.start, end: week.end, isCurrent: week.start === currentWeek.start, isOffWeek, days });
  }

  return weeks;
}
